import { useState } from 'react';
import { ModelStateList, modelState } from '@/utils/modelState';
import { BookingPeriodConditionTypes } from './BookingPeriodConditionTypes/BookingPeriodConditionType';
import { QueryBuilder } from './QueryBuilder';
import { ScheduleConditionComponent } from './ScheduleConditionComponent';
import { ScheduleEvent } from './ScheduleWrapperComponent';
import { v4 as uuidv4 } from 'uuid';
import { useModelState } from '@/utils/useModelState';
import { DateBookingPeriodConditionTypeComponent } from './DateBookingPeriodConditionTypeComponent';
import { YearlyDateBookingPeriodConditionTypeComponent } from './YearlyDateBookingPeriodConditionTypeComponent';
import { MonthsBookingPeriodConditionTypeComponent } from './MonthsBookingPeriodConditionTypeComponent';

export namespace BookingPeriodCondition {

    export const registryEntry: QueryBuilder.ConditionRegistryEntry = {
        key: "BookingPeriod",
        name: "Booking Period",
        isCondition: isBookingPeriodCondition,
        create: createBookingPeriodCondition,
        hydrate: hydrateBookingPeriodCondition,
        serialize: serializeBookingPeriodCondition,
        deserialize: deserializeBookingPeriodCondition,
        renderComponent: (condition: BookingPeriodCondition) => (<BookingPeriodConditionComponent condition={condition} />)
    };

    export const BookingPeriodCondition = Symbol("BookingPeriodCondition");
    export interface BookingPeriodCondition extends QueryBuilder.ConditionNodeChild {
        [BookingPeriodCondition]: boolean;
        conditionType: modelState<BookingPeriodConditionTypes.BookingPeriodConditionType>;
    }

    export function isBookingPeriodCondition(condition?: QueryBuilder.ConditionNodeChild): condition is BookingPeriodCondition {
        if (!condition) {
            return false;
        }

        return (condition as BookingPeriodCondition)[BookingPeriodCondition] === true;
    }

    export function createBookingPeriodCondition(skipHydration: boolean = false): BookingPeriodCondition {

        const condition: BookingPeriodCondition = {
            [QueryBuilder.Node]: true,
            [QueryBuilder.ConditionNodeChild]: true,
            [BookingPeriodCondition]: true,
            id: uuidv4(),
            version: modelState(0),
            conditionType: modelState(BookingPeriodConditionTypes.ScheduleBookingPeriodConditionTypeDefinition.create()),
            dispose: () => { }
        };

        if (!skipHydration) {
            hydrateBookingPeriodCondition(condition);
        }

        return condition;
    }

    export function hydrateBookingPeriodCondition(condition: BookingPeriodCondition, recursive: boolean = false) {


        // Condition type
        let oldConditionTypeSubscription: () => void = null;

        function subscribeConditionType() {
            if (condition.conditionType.value) {
                oldConditionTypeSubscription = condition.conditionType.value.version.subscribe(() => {
                    condition.version.setValue(condition.version.value + 1);
                });
            }
        }

        subscribeConditionType();

        const unsubscribeConditionType = condition.conditionType.subscribe(() => {
            oldConditionTypeSubscription?.();

            subscribeConditionType();

            condition.version.setValue(condition.version.value + 1);
        });

        const oldDispose = condition.dispose;

        condition.dispose = () => {
            unsubscribeConditionType();

            oldDispose();
        };

        if (recursive) {
            if (condition.conditionType.value) {
                BookingPeriodConditionTypes.getBookingPeriodConditionTypeDefinition(condition.conditionType.value).hydrate(condition.conditionType.value, true);
            }
        }
    }

    function serializeBookingPeriodCondition(condition: BookingPeriodCondition): QueryBuilder.ConditionChildNodeDtoWrapper {
        const conditionDto: BookingPeriodConditionDto = {
            NodeId: condition.id,
            ConditionType: BookingPeriodConditionTypes.serializeBookingPeriodConditionType(condition.conditionType.value)
        };

        return {
            Type: "BookingPeriod",
            Value: conditionDto
        };
    }

    interface BookingPeriodConditionDto extends QueryBuilder.NodeDto {
        ConditionType: BookingPeriodConditionTypes.BookingPeriodConditionTypeDto;
    }

    function deserializeBookingPeriodCondition(conditionDto: BookingPeriodConditionDto): BookingPeriodCondition {
        const condition = createBookingPeriodCondition(true);

        condition.conditionType.setValue(BookingPeriodConditionTypes.deserializeBookingPeriodConditionType(conditionDto.ConditionType));
        condition.conditionType.commit();

        return condition;
    }

    function BookingPeriodConditionComponent({ condition }: { condition: BookingPeriodCondition }) {
        const [conditionType, setConditionType] = useModelState<BookingPeriodConditionTypes.BookingPeriodConditionType>(condition.conditionType)

        return (
            <div className="flex flex-row gap-2">
                <div>
                    <select className="form-control" value={BookingPeriodConditionTypes.getBookingPeriodConditionTypeDefinition(conditionType).key} onChange={e => {
                        setConditionType(BookingPeriodConditionTypes.createBookingPeriodConditionType(e.target.value));
                    }}>
                        {BookingPeriodConditionTypes.bookingPeriodConditionTypes.map(t => (
                            <option key={t.key} value={t.key}>{t.name}</option>
                        ))}
                    </select>
                </div>
                {BookingPeriodConditionTypes.isScheduleBookingPeriodConditionType(condition.conditionType.value) && (
                    <div>
                        <ScheduleConditionComponent events={condition.conditionType.value.schedule} />
                    </div>
                )}
                {BookingPeriodConditionTypes.isDateBookingPeriodConditionType(condition.conditionType.value) && (
                    <div>
                        <DateBookingPeriodConditionTypeComponent conditionType={condition.conditionType.value} />
                    </div>
                )}
                {BookingPeriodConditionTypes.isYearlyDateBookingPeriodConditionType(condition.conditionType.value) && (
                    <div>
                        <YearlyDateBookingPeriodConditionTypeComponent conditionType={condition.conditionType.value} />
                    </div>
                )}
                {BookingPeriodConditionTypes.isMonthsBookingPeriodConditionType(condition.conditionType.value) && (
                    <div>
                        <MonthsBookingPeriodConditionTypeComponent conditionType={condition.conditionType.value} />
                    </div>
                )}
            </div>
        );
    }
}
